import { LocationDescriptor } from 'history';
import {
  DESTINATION_PRESETS,
  DestinationPlatformMap,
  PlatformVideoType,
} from 'blocks/DestinationPlatforms';
import { getAudiogramWizardPath, getFullEpisodeWizardPath } from 'utils/routes';

type VideoTypeDestinationConfig = Partial<
  Record<PlatformVideoType, LocationDescriptor>
>;

const YOUTUBE_AUDIOGRAM_DESTINATION_CONFIG: VideoTypeDestinationConfig = {
  episode: getFullEpisodeWizardPath({
    defaultAspectRatio:
      DESTINATION_PRESETS.youtube.episode.aspectRatio.audiogram,
  }),
  clip: getAudiogramWizardPath({
    defaultAspectRatio: DESTINATION_PRESETS.youtube.clip.aspectRatio.audiogram,
    defaultClipDurationMillis:
      DESTINATION_PRESETS.youtube.clip.durationMs.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.youtube.clip.key,
  }),
  short: getAudiogramWizardPath({
    defaultAspectRatio: DESTINATION_PRESETS.youtube.short.aspectRatio.audiogram,
    defaultClipDurationMillis:
      DESTINATION_PRESETS.youtube.short.durationMs.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.youtube.short.key,
  }),
};

const INSTAGRAM_AUDIOGRAM_DESTINATION_CONFIG: VideoTypeDestinationConfig = {
  reel: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.instagram.reel.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.instagram.reel.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.instagram.reel.key,
  }),
  story: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.instagram.story.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.instagram.story.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.instagram.story.key,
  }),
  post: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.instagram.post.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.instagram.post.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.instagram.post.key,
  }),
};

const FACEBOOK_AUDIOGRAM_DESTINATION_CONFIG: VideoTypeDestinationConfig = {
  reel: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.facebook.reel.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.facebook.reel.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.facebook.reel.key,
  }),
  story: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.facebook.story.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.facebook.story.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.facebook.story.key,
  }),
  post: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.facebook.post.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.facebook.post.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.facebook.post.key,
  }),
  episode: getFullEpisodeWizardPath({
    defaultAspectRatio:
      DESTINATION_PRESETS.facebook.episode.aspectRatio.audiogram,
  }),
};

const TIKTOK_AUDIOGRAM_DESTINATION_CONFIG: VideoTypeDestinationConfig = {
  post: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.tiktok.post.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.tiktok.post.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.tiktok.post.key,
  }),
  story: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.tiktok.story.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.tiktok.story.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.tiktok.story.key,
  }),
};

const LINKEDIN_AUDIOGRAM_DESTINATION_CONFIG: VideoTypeDestinationConfig = {
  feedVideo: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.linkedin.feedVideo.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.linkedin.feedVideo.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.linkedin.feedVideo.key,
  }),
  post: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.linkedin.post.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.linkedin.post.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.linkedin.post.key,
  }),
  short: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.linkedin.short.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.linkedin.short.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.linkedin.short.key,
  }),
};

const THREADS_AUDIOGRAM_DESTINATION_CONFIG: VideoTypeDestinationConfig = {
  reel: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.threads.reel.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.threads.reel.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.threads.reel.key,
  }),
  post: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.threads.post.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.threads.post.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.threads.post.key,
  }),
};

const TWITTER_AUDIOGRAM_DESTINATION_CONFIG: VideoTypeDestinationConfig = {
  standardTimeline: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.twitter.standardTimeline.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.twitter.standardTimeline.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.twitter.standardTimeline.key,
  }),
  post: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.twitter.post.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.twitter.post.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.twitter.post.key,
  }),
  videoTab: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.twitter.videoTab.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.twitter.videoTab.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.twitter.videoTab.key,
  }),
};

const BLUESKY_AUDIOGRAM_DESTINATION_CONFIG: VideoTypeDestinationConfig = {
  post: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.bluesky.post.durationMs.audiogram,
    defaultAspectRatio: DESTINATION_PRESETS.bluesky.post.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.bluesky.post.key,
  }),
  shortClip: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.bluesky.shortClip.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.bluesky.shortClip.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.bluesky.shortClip.key,
  }),
  videoFeeds: getAudiogramWizardPath({
    defaultClipDurationMillis:
      DESTINATION_PRESETS.bluesky.videoFeeds.durationMs.audiogram,
    defaultAspectRatio:
      DESTINATION_PRESETS.bluesky.videoFeeds.aspectRatio.audiogram,
    defaultPresetKey: DESTINATION_PRESETS.bluesky.videoFeeds.key,
  }),
};

export const AUDIOGRAM_PLATFORMS_DESTINATION_CONFIG: DestinationPlatformMap<VideoTypeDestinationConfig> = {
  youtube: YOUTUBE_AUDIOGRAM_DESTINATION_CONFIG,
  instagram: INSTAGRAM_AUDIOGRAM_DESTINATION_CONFIG,
  facebook: FACEBOOK_AUDIOGRAM_DESTINATION_CONFIG,
  tiktok: TIKTOK_AUDIOGRAM_DESTINATION_CONFIG,
  linkedin: LINKEDIN_AUDIOGRAM_DESTINATION_CONFIG,
  threads: THREADS_AUDIOGRAM_DESTINATION_CONFIG,
  twitter: TWITTER_AUDIOGRAM_DESTINATION_CONFIG,
  bluesky: BLUESKY_AUDIOGRAM_DESTINATION_CONFIG,
};
